import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Heart, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSupporterStatus } from '../services/supporterService';
import { KOFI_CONFIG } from '../config/kofi';
import { getScanCount } from '../utils/scanCounter';

const DISMISSED_AT_KEY = 'colobongo_love_nudge_dismissed_at';
const SEEN_COUNT_KEY = 'colobongo_love_nudge_seen_count';
const CLICKED_AT_KEY = 'colobongo_love_nudge_clicked_at';
const SESSION_SEEN_KEY = 'colobongo_love_nudge_seen';

const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const COMPACT_VIEWPORT_MAX_WIDTH = 380;
const NUDGE_POST_SCAN_DELAY_MS = 3000;

const ROUTE_EXCLUSIONS = [
  '/auth',
  '/onboarding',
  '/admin',
  '/privacy',
  '/terms',
  '/cookies',
  '/settings',
  '/install'
];

const ROUTE_INCLUSIONS = [
  '/',
  '/wheel',
  '/challenge',
  '/palettes',
  '/studio',
  '/gallery',
  '/textures',
  '/mur'
];

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

function isRouteIncluded(pathname) {
  return ROUTE_INCLUSIONS.some((basePath) => {
    if (basePath === '/') return pathname === '/';
    return pathname === basePath || pathname.startsWith(`${basePath}/`);
  });
}

function isRouteExcluded(pathname) {
  return ROUTE_EXCLUSIONS.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function isEligibleRoute(pathname) {
  return isRouteIncluded(pathname) && !isRouteExcluded(pathname);
}

function hasActiveBlockingOverlay() {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector('.fixed.inset-0.z-50, [role="dialog"][aria-modal="true"]'));
}

function getStoredNumber(storage, key) {
  try {
    const value = storage.getItem(key);
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  } catch {
    return null;
  }
}

function setStoredValue(storage, key, value) {
  try {
    storage.setItem(key, String(value));
  } catch {
    // no-op on restricted storage
  }
}

function incrementSeenCount() {
  if (typeof window === 'undefined') return;
  const current = getStoredNumber(window.localStorage, SEEN_COUNT_KEY) || 0;
  setStoredValue(window.localStorage, SEEN_COUNT_KEY, current + 1);
}

export default function LoveColobongoNudge({ language = 'fr', enabled = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const { isContributorActive, loading } = useSupporterStatus();
  const [visible, setVisible] = useState(false);
  const [compactCopy, setCompactCopy] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= COMPACT_VIEWPORT_MAX_WIDTH;
  });

  const normalizedPath = useMemo(() => normalizePath(location.pathname), [location.pathname]);
  const routeAllowed = useMemo(() => isEligibleRoute(normalizedPath), [normalizedPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onResize = () => {
      setCompactCopy(window.innerWidth <= COMPACT_VIEWPORT_MAX_WIDTH);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (loading || !enabled || isContributorActive || !routeAllowed) {
      setVisible(false);
      return undefined;
    }

    if (typeof window === 'undefined') return undefined;

    // Condition scan : ne se déclenche qu'après le 2e scan réussi
    if (getScanCount() < 2) return undefined;

    // Ne jamais afficher deux fois dans la même session
    if (window.sessionStorage.getItem(SESSION_SEEN_KEY)) {
      return undefined;
    }

    // Cooldown après dismissal (7 jours)
    const dismissedAt = getStoredNumber(window.localStorage, DISMISSED_AT_KEY);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
      return undefined;
    }

    // Délai court pour ne pas s'afficher instantanément après le scan
    const timer = window.setTimeout(() => {
      if (hasActiveBlockingOverlay()) return;
      setVisible(true);
      setStoredValue(window.sessionStorage, SESSION_SEEN_KEY, '1');
      incrementSeenCount();
    }, NUDGE_POST_SCAN_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [enabled, isContributorActive, loading, routeAllowed]);

  const closeForNow = useCallback(() => {
    setVisible(false);
    if (typeof window === 'undefined') return;
    setStoredValue(window.localStorage, DISMISSED_AT_KEY, Date.now());
  }, []);

  const handleSupportClick = useCallback(() => {
    if (typeof window === 'undefined') return;

    // The "send some love" nudge points at the Ko-fi tip jar (one-time
    // donation), never at a subscription page. Subscriptions go through
    // /soutenir (Stripe).
    const targetUrl = KOFI_CONFIG.coffeeUrl;
    if (targetUrl) {
      setStoredValue(window.localStorage, CLICKED_AT_KEY, Date.now());
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }

    closeForNow();
  }, [closeForNow]);

  const handleContributorClick = useCallback(() => {
    if (typeof window !== 'undefined') {
      setStoredValue(window.localStorage, CLICKED_AT_KEY, Date.now());
    }
    closeForNow();
    navigate('/soutenir');
  }, [closeForNow, navigate]);

  const copy = language === 'en'
    ? {
      title: 'A little love for ColoBongo?',
      body: 'The app grows with the Colomunauty. Your support helps Bongo create more cozy tools \u{1F49B}',
      compact: 'Help ColoBongo grow \u{1F49B}',
      primary: 'Send some love',
      contributor: 'Become a Contributor',
      secondary: 'Later',
      closeLabel: 'Close support nudge'
    }
    : {
      title: 'Un peu d\'amour pour ColoBongo ?',
      body: 'L\'app grandit grâce à la Colomunauté. Ton soutien aide Bongo à créer encore plus d\'outils cozy \u{1F49B}',
      compact: 'Aide ColoBongo à grandir \u{1F49B}',
      primary: 'Envoyer de l\'amour',
      contributor: 'Devenir Contributeur',
      secondary: 'Plus tard',
      closeLabel: 'Fermer cette invitation'
    };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          initial={{ opacity: 0, y: 14, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="love-colobongo-nudge fixed left-3 right-3 sm:left-auto sm:right-4 sm:max-w-[330px]"
          style={{ zIndex: 9990 }}
          role="complementary"
          aria-live="polite"
        >
          <motion.div
            animate={prefersReducedMotion ? undefined : { y: [0, -2, 0] }}
            transition={prefersReducedMotion ? undefined : { duration: 5.4, ease: 'easeInOut', repeat: Infinity }}
            className="cozy-card love-colobongo-nudge__card p-3.5"
          >
            <button
              type="button"
              onClick={closeForNow}
              className="love-colobongo-nudge__close"
              aria-label={copy.closeLabel}
            >
              <X size={14} />
            </button>

            <div className="pr-8">
              <p className="love-colobongo-nudge__title">
                <Heart size={14} className="love-colobongo-nudge__heart" aria-hidden="true" />
                <span>{copy.title}</span>
              </p>

              <p className="love-colobongo-nudge__body">
                {compactCopy ? copy.compact : copy.body}
              </p>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSupportClick}
                className="love-colobongo-nudge__primary"
              >
                {copy.primary}
              </button>
              <button
                type="button"
                onClick={handleContributorClick}
                className="love-colobongo-nudge__contributor"
              >
                {copy.contributor}
              </button>
              <button
                type="button"
                onClick={closeForNow}
                className="love-colobongo-nudge__tertiary"
              >
                {copy.secondary}
              </button>
            </div>
          </motion.div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
